'use client';

import { useCallback, useMemo, useState } from 'react';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import {
  ColumnDef,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import {
  ChevronDown,
  Download,
  Globe,
  Loader2,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type * as XLSXType from 'xlsx';
import { trackSearchResultClick } from '@/lib/analytics';
import { LegalStatusBadge } from './LegalStatusBadge';
import { companyCountryCode, generateCompanySlug } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { CompanyCountryFlag } from '@/new_components/shared/CompanyCountryFlag';
import { useSettings } from '@/providers/settings-provider';
import { useLocale } from '@/hooks/use-locale';
import { LocaleLink } from '@/components/locale-link';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridPagination } from '@/components/ui/data-grid-pagination';
import { DataGridTable } from '@/components/ui/data-grid-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  backfillEmployeesBatchAction,
  backfillWebsiteBatchAction,
} from '@/app/actions/backfill-actions';
import { AddToListMenu } from '@/new_components/lists/AddToListMenu';
import { CompanyCard, CompanyCardData } from './CompanyCard';
import { CompanySearchResult } from './companySearchService';
import { SearchInteractionProvider } from './SearchInteractionContext';

interface CompanyResultsTableProps {
  previewEuId?: string | null;
  onOpenPreview?: (euId: string, trigger?: HTMLElement) => void;
  results: CompanySearchResult[];
  total: number;
  totalCapped?: boolean;
  offset: number;
  limit?: number;
  sort?: string;
  financialYear?: number;
  // Whether the user may export (Pro or Admin). The export button is shown to
  // everyone; non-entitled users get a "Pro feature" prompt instead.
  canExport?: boolean;
  // Whether the user may run the enrich (website/employee) batch actions —
  // Pro or Admin. The buttons are hidden entirely for everyone else.
  canEnrich?: boolean;
  // Whether the user may customize visible columns (Pro or Admin). The Columns
  // button is shown to everyone; non-entitled users get a "Pro feature" prompt.
  canCustomizeColumns?: boolean;
  // Fetches up to `limit` rows for export using the active filters/sort.
  // Required for the export dialog to do anything beyond the current page.
  fetchResultsForExport?: (limit: number) => Promise<CompanySearchResult[]>;
  // Called after an employee enrich with the fresh per-company counts so the
  // parent can overlay them on the displayed rows (mv_search can't be written
  // per-click). Keyed by euId → latest-year total.
  onEmployeeCountsUpdated?: (counts: Record<string, number>) => void;
  isLoading?: boolean;
  onSortChange?: (sort: string) => void;
  onOffsetChange?: (offset: number) => void;
  // Change the page size (results per page). Wired to the results-per-page
  // dropdown in the bottom pagination bar.
  onLimitChange?: (limit: number) => void;
  onRefresh?: () => void;
  // Fired when the export button is clicked — for every user (incl. guests and
  // non-entitled users), before the Pro check. Used for usage analytics.
  onExportClick?: () => void;
  // Fired after an export file is generated successfully, with what was
  // exported so the parent can log it alongside the active filters.
  onExportSuccess?: (info: { exportCount: number; columns: string[] }) => void;
  // The search's country scope ('DE' | 'CH'; undefined = global). Swiss
  // companies carry no financials, industry or contact data, so 'CH' swaps
  // in a default column set built from what the Swiss register does have.
  country?: string;
}

const numberFormatter = new Intl.NumberFormat('de-DE');
const secondaryTextClass = 'text-sm text-muted-foreground dark:text-zinc-300';
const MOBILE_PAGE_SIZE = 10;

// Where logged-in users' column choices are persisted (localStorage via
// SettingsProvider). Guests always see the default set below.
const VISIBLE_COLUMNS_SETTING = 'search.visibleColumns';
// Swiss choices live under their own key so they never overwrite the German
// ones and vice versa. A sibling, deliberately not `search.visibleColumns.CH`:
// SettingsProvider treats dots as a path, so a nested key would sit inside
// the German visibility object and be wiped every time that is stored.
const VISIBLE_COLUMNS_SETTING_CH = 'search.visibleColumnsCH';

// Columns a logged-in user can toggle, in dropdown order. `name` and the `#`
// index column are intentionally absent — they are always visible anchors.
// `labelKey` maps to an i18n key under `search.table`.
const TOGGLEABLE_COLUMNS: { id: string; labelKey: string }[] = [
  { id: 'city', labelKey: 'city' },
  { id: 'bundesland', labelKey: 'state' },
  { id: 'rechtsform', labelKey: 'rechtsform' },
  { id: 'industrySector', labelKey: 'industry' },
  { id: 'naceCode', labelKey: 'naceCode' },
  { id: 'employees', labelKey: 'employees' },
  { id: 'totalRevenue', labelKey: 'revenue' },
  { id: 'profit', labelKey: 'profit' },
  { id: 'domain', labelKey: 'website' },
  { id: 'email', labelKey: 'email' },
  { id: 'registerType', labelKey: 'registerType' },
  { id: 'registerId', labelKey: 'registerId' },
  { id: 'registerCourt', labelKey: 'registerCourt' },
  { id: 'kreis', labelKey: 'kreis' },
  { id: 'postalCode', labelKey: 'postalCode' },
  { id: 'cpvCodes', labelKey: 'cpvCodes' },
];

// Default visibility: the eight columns shown today stay on; the additional
// mv_search columns are available but off until the user enables them.
const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  // Off by default — the inline badge on the name already surfaces a
  // non-active status, so the column is for scanning and export.
  legalStatus: false,
  city: true,
  bundesland: true,
  rechtsform: false,
  industrySector: true,
  naceCode: false,
  employees: true,
  totalRevenue: true,
  profit: true,
  domain: true,
  email: true,
  registerType: false,
  registerId: false,
  registerCourt: false,
  kreis: false,
  postalCode: false,
  cpvCodes: false,
};

// Swiss scope (country=CH). Measured on mv_search: Swiss rows have city,
// canton, legal form, UID, register office and legal status throughout and a
// postal code for a good share — but no financials, headcount, industry,
// website, email or Kreis. Defaulting to the German set would show a grid of
// dashes, so the columns that carry data are on and the rest stay toggleable.
const SWISS_DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  legalStatus: true,
  city: true,
  bundesland: true,
  rechtsform: true,
  registerId: true,
  registerCourt: true,
  postalCode: true,
  industrySector: false,
  naceCode: false,
  employees: false,
  totalRevenue: false,
  profit: false,
  domain: false,
  email: false,
  registerType: false,
  kreis: false,
  cpvCodes: false,
};

// Swiss wording for columns whose German label names a German concept: the
// `bundesland` field holds the canton, the register id is the CHE-UID, and
// the "court" is a cantonal commercial register office. Keyed by column id,
// valued by an i18n key under `search.table`.
const SWISS_LABEL_KEYS: Record<string, string> = {
  bundesland: 'canton',
  registerId: 'uid',
  registerCourt: 'registerOffice',
};

function columnLabelKey(
  id: string,
  defaultKey: string,
  country?: string,
): string {
  return (country === 'CH' && SWISS_LABEL_KEYS[id]) || defaultKey;
}

// Columns offered in the Excel export dialog, in output order. `labelKey` maps
// to an i18n key under `search.table` (euId is special-cased to "EU-ID").
const EXPORTABLE_COLUMNS: {
  id: string;
  labelKey: string;
  value: (r: CompanySearchResult) => string | number;
}[] = [
  { id: 'name', labelKey: 'name', value: (r) => r.name ?? '' },
  { id: 'city', labelKey: 'city', value: (r) => r.city ?? '' },
  {
    id: 'legalStatus',
    labelKey: 'legalStatus',
    value: (r) => r.legalStatus ?? 'active',
  },
  { id: 'bundesland', labelKey: 'state', value: (r) => r.bundesland ?? '' },
  { id: 'rechtsform', labelKey: 'rechtsform', value: (r) => r.rechtsform ?? '' },
  { id: 'kreis', labelKey: 'kreis', value: (r) => r.kreis ?? '' },
  { id: 'postalCode', labelKey: 'postalCode', value: (r) => r.postalCode ?? '' },
  {
    id: 'industrySector',
    labelKey: 'industry',
    value: (r) => r.industrySector ?? '',
  },
  { id: 'naceCode', labelKey: 'naceCode', value: (r) => r.naceCode ?? '' },
  { id: 'employees', labelKey: 'employees', value: (r) => r.employees ?? '' },
  // The financial figures carry their own fiscal year, because each one is
  // resolved to the most recent filing that actually reports it — a row's
  // revenue can be from 2021 while its profit is from 2024. The grid omits
  // the years to stay readable and because the company page shows the full
  // history, but an exported sheet leaves the product entirely, so the
  // columns are offered here. Off by default in DEFAULT_EXPORT_COLUMNS.
  {
    id: 'totalRevenue',
    labelKey: 'revenue',
    value: (r) => r.totalRevenue ?? '',
  },
  {
    id: 'totalRevenueYear',
    labelKey: 'revenueYear',
    value: (r) => r.totalRevenueYear ?? '',
  },
  { id: 'profit', labelKey: 'profit', value: (r) => r.profit ?? '' },
  {
    id: 'profitYear',
    labelKey: 'profitYear',
    value: (r) => r.profitYear ?? '',
  },
  {
    id: 'totalBalanceSum',
    labelKey: 'aktiva',
    value: (r) => r.totalBalanceSum ?? '',
  },
  {
    id: 'totalBalanceSumYear',
    labelKey: 'aktivaYear',
    value: (r) => r.totalBalanceSumYear ?? '',
  },
  {
    id: 'cpvCodes',
    labelKey: 'cpvCodes',
    value: (r) => r.cpvCodes?.join(', ') ?? '',
  },
  {
    id: 'registerType',
    labelKey: 'registerType',
    value: (r) => r.registerType ?? '',
  },
  {
    id: 'registerId',
    labelKey: 'registerId',
    value: (r) => r.registerId ?? '',
  },
  {
    id: 'registerCourt',
    labelKey: 'registerCourt',
    value: (r) => r.registerCourt ?? '',
  },
  { id: 'domain', labelKey: 'website', value: (r) => r.domain ?? '' },
  { id: 'email', labelKey: 'email', value: (r) => r.email ?? '' },
  { id: 'euId', labelKey: 'euId', value: (r) => r.euId ?? '' },
];

// Default-checked columns reproduce the previous fixed export output.
const DEFAULT_EXPORT_COLUMNS = [
  'name',
  'city',
  'bundesland',
  'industrySector',
  'employees',
  'totalRevenue',
  'profit',
  'cpvCodes',
  'domain',
  'email',
  'euId',
];

// Swiss scope: the columns that carry data for Swiss companies (see
// SWISS_DEFAULT_COLUMN_VISIBILITY).
const SWISS_DEFAULT_EXPORT_COLUMNS = [
  'name',
  'legalStatus',
  'city',
  'bundesland',
  'rechtsform',
  'postalCode',
  'registerId',
  'registerCourt',
  'euId',
];

const EXPORT_COUNT_PRESETS = [100, 1000, 5000, 10000];
const EXPORT_MAX = 10000;

function exportColumnLabel(
  id: string,
  t: ReturnType<typeof useTranslation>['t'],
  country?: string,
): string {
  if (id === 'euId') return 'EU-ID';
  const col = EXPORTABLE_COLUMNS.find((c) => c.id === id);
  return col
    ? t(`search.table.${columnLabelKey(col.id, col.labelKey, country)}`)
    : id;
}

// Local-time timestamp for export filenames, e.g. 2026-06-01_14-30-22.
function fileTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}

async function exportToExcel(
  rows: CompanySearchResult[],
  columnIds: string[],
  t: ReturnType<typeof useTranslation>['t'],
  financialYear?: number,
  country?: string,
) {
  const XLSX: typeof XLSXType = await import('xlsx');
  // Keep canonical column order regardless of the order ids were toggled in.
  const cols = EXPORTABLE_COLUMNS.filter((c) => columnIds.includes(c.id));
  const data = rows.map((r, i) => {
    const row: Record<string, string | number> = { '#': i + 1 };
    for (const c of cols)
      row[exportColumnLabel(c.id, t, country)] = c.value(r);
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Companies');
  const yearSuffix = financialYear ? `_${financialYear}` : '';
  XLSX.writeFile(wb, `company_search${yearSuffix}_${fileTimestamp()}.xlsx`);
}

function SortIcon({ field, sort }: { field: string; sort?: string }) {
  const isActive = sort?.startsWith(field);
  const isAsc = sort === `${field}-asc`;
  return (
    <ChevronDown
      className={cn(
        'w-3 h-3 transition-all',
        isActive ? 'opacity-100' : 'opacity-20',
        isAsc && 'rotate-180',
      )}
    />
  );
}

export default function CompanyResultsTable({
  results,
  total,
  totalCapped = false,
  offset,
  limit = 50,
  sort,
  financialYear,
  canExport = false,
  canEnrich = false,
  canCustomizeColumns = false,
  fetchResultsForExport,
  onEmployeeCountsUpdated,
  isLoading = false,
  onSortChange,
  onOffsetChange,
  onLimitChange,
  onRefresh,
  onExportClick,
  onExportSuccess,
  country,
  previewEuId,
  onOpenPreview,
}: CompanyResultsTableProps) {
  const { t, i18n } = useTranslation();
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);
  const totalLabel =
    fmt.fullNumber.format(total) + (totalCapped ? t('search.totalCappedSuffix') : '');
  const { locale } = useLocale();
  const { getOption, storeOption } = useSettings();
  const [isEnrichingWebsites, setIsEnrichingWebsites] = useState(false);
  const [isEnrichingEmployees, setIsEnrichingEmployees] = useState(false);
  const [showProDialog, setShowProDialog] = useState(false);
  // Which feature triggered the Pro prompt — drives the dialog's description.
  const [proDialogFeature, setProDialogFeature] = useState<
    'export' | 'columns' | 'enrich'
  >('export');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  // Row selection, keyed by eu_id so it survives paging and re-sorting.
  // Deliberately NOT cleared when the page changes: picking three companies
  // on page 1 and two on page 2 before adding them to a list is the whole
  // point of selecting across a result set.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedEuIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const openProDialog = useCallback(
    (feature: 'export' | 'columns' | 'enrich') => {
      setProDialogFeature(feature);
      setShowProDialog(true);
    },
    [],
  );

  const handleExportClick = useCallback(() => {
    // Log every click — including guests and non-entitled users — before the
    // Pro gate, so we can see demand for the export feature.
    onExportClick?.();
    if (!canExport) {
      openProDialog('export');
      return;
    }
    setExportError(false);
    setShowExportDialog(true);
  }, [canExport, onExportClick, openProDialog]);

  const runExport = useCallback(
    async (columnIds: string[], count: number) => {
      setExportError(false);
      setIsExporting(true);
      try {
        // Fall back to the current page if no fetcher was provided.
        const rows = fetchResultsForExport
          ? await fetchResultsForExport(count)
          : results.slice(0, count);
        await exportToExcel(rows, columnIds, t, financialYear, country);
        onExportSuccess?.({ exportCount: rows.length, columns: columnIds });
        setShowExportDialog(false);
      } catch {
        setExportError(true);
      } finally {
        setIsExporting(false);
      }
    },
    [fetchResultsForExport, results, financialYear, t, onExportSuccess, country],
  );

  // Column visibility. Only Pro/Admin may customize columns, so only they get
  // their persisted choice merged over the defaults; everyone else always sees
  // the defaults. `name`/`index` are forced visible so a stale stored value can
  // never hide the anchor columns. Defaults and the stored choice are both
  // per country scope: Swiss (CH) has its own of each, DE and Global share.
  const isSwiss = country === 'CH';
  const visibilitySetting = isSwiss
    ? VISIBLE_COLUMNS_SETTING_CH
    : VISIBLE_COLUMNS_SETTING;
  const defaultVisibility = isSwiss
    ? SWISS_DEFAULT_COLUMN_VISIBILITY
    : DEFAULT_COLUMN_VISIBILITY;
  const storedVisibility = getOption<VisibilityState | undefined>(
    visibilitySetting,
  );
  const columnVisibility = useMemo<VisibilityState>(() => {
    const base = canCustomizeColumns
      ? { ...defaultVisibility, ...(storedVisibility ?? {}) }
      : { ...defaultVisibility };
    return { ...base, name: true, index: true };
  }, [canCustomizeColumns, storedVisibility, defaultVisibility]);

  const handleColumnVisibilityChange = useCallback(
    (id: string, visible: boolean) => {
      storeOption(visibilitySetting, {
        ...columnVisibility,
        [id]: visible,
      });
    },
    [columnVisibility, storeOption, visibilitySetting],
  );

  const handleSort = useCallback(
    (field: string) => {
      if (!onSortChange) return;

      const toggleFields = ['profit'];
      let newSort: string;
      if (sort === `${field}-desc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-asc`
          : 'revenue-desc';
      } else if (sort === `${field}-asc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-desc`
          : 'revenue-desc';
      } else {
        newSort = `${field}-desc`;
      }
      onSortChange(newSort);
    },
    [onSortChange, sort],
  );

  const handleEnrichWebsites = useCallback(async () => {
    if (!canEnrich) {
      openProDialog('enrich');
      return;
    }
    const euIds = results.filter((r) => !r.domain).map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingWebsites(true);
    try {
      await backfillWebsiteBatchAction(euIds);
      onRefresh?.();
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingWebsites(false);
    }
  }, [results, canEnrich, openProDialog]);

  const handleEnrichEmployees = useCallback(async () => {
    if (!canEnrich) {
      openProDialog('enrich');
      return;
    }
    const euIds = results.map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingEmployees(true);
    try {
      const res = await backfillEmployeesBatchAction(euIds);
      // Overlay the freshly-parsed counts onto the displayed rows. We don't
      // call onRefresh here — that would re-read the (still stale) mv_search.
      if (res.success && res.results) {
        const counts: Record<string, number> = {};
        for (const r of res.results) {
          if (r.status !== 'ok' || !r.employee_data?.length) continue;
          // Latest year wins, matching how mv_search picks employee_count.
          const latest = r.employee_data
            .filter((d) => d.gesamt != null)
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
          if (latest?.gesamt != null) counts[r.eu_id] = latest.gesamt;
        }
        onEmployeeCountsUpdated?.(counts);
      }
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingEmployees(false);
    }
  }, [results, canEnrich, openProDialog, onEmployeeCountsUpdated]);

  const columns = useMemo<ColumnDef<CompanySearchResult>[]>(() => {
    // Columns to hide on mobile — only Name and City are shown by default.
    const MOBILE_HIDE = new Set([
      'legalStatus',
      'bundesland',
      'rechtsform',
      'industrySector',
      'naceCode',
      'cpvCodes',
      'employees',
      'totalRevenue',
      'profit',
      'domain',
      'email',
      'registerType',
      'registerId',
      'registerCourt',
      'kreis',
      'postalCode',
    ]);
    const defs: ColumnDef<CompanySearchResult>[] = [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            aria-label={t('search.selectAllOnPage', 'Select all on this page')}
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(value === true)
            }
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={t('search.selectRow', 'Select company')}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            // The row itself navigates to the company page.
            onClick={(e) => e.stopPropagation()}
          />
        ),
        size: 40,
        enableResizing: false,
      },
      {
        id: 'index',
        header: () => (
          <span className="text-muted-foreground w-8 block text-center">#</span>
        ),
        cell: (info) => (
          <span className="text-muted-foreground w-8 block text-center text-xs">
            {offset + info.row.index + 1}
          </span>
        ),
        size: 50,
        enableResizing: false,
      },
      {
        accessorKey: 'name',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('name')}
          >
            <span>{t('search.table.name')}</span>
            <SortIcon field="name" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const company = info.row.original;
          const faviconUrl = (
            company as CompanySearchResult & { faviconUrl?: string }
          ).faviconUrl;
          const slug = generateCompanySlug(
            {
              name: company.name,
              sitz: company.city,
              registerCourt: company.registerCourt,
              registerType: company.registerType,
              registerId: company.registerId,
            },
            locale,
          );
          return (
            <LocaleLink
              href={slug}
              className="flex items-center gap-2"
              onClick={(e) => {
                const position = results.findIndex((r) => r.euId === company.euId);
                trackSearchResultClick(company.euId, offset + position);
                e.stopPropagation();
                if (
                  onOpenPreview && e.button === 0 &&
                  !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
                ) {
                  e.preventDefault();
                  onOpenPreview(company.euId, e.currentTarget);
                }
              }}
              onAuxClick={(e) => e.stopPropagation()}
            >
              {faviconUrl && (
                <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/95 dark:bg-white border border-black/5 dark:border-white/20 shrink-0">
                  <img
                    src={faviconUrl}
                    alt={`${info.getValue() as string} favicon`}
                    className="h-3.5 w-3.5 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </span>
              )}
              <span
                className="font-medium text-sm text-foreground truncate block max-w-[180px] lg:max-w-[300px] hover:text-primary hover:underline transition-colors"
                title={info.getValue() as string}
              >
                {info.getValue() as string}
              </span>
              {/* Shown inline rather than only as a toggleable column: a
                  company being insolvent changes what the row means, and
                  users should not have to enable a column to find that out. */}
              <LegalStatusBadge status={company.legalStatus} />
            </LocaleLink>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'legalStatus',
        header: () => <span>{t('search.table.legalStatus', 'Status')}</span>,
        cell: (info) => {
          const status = info.getValue() as string;
          if (!status || status === 'active') {
            return (
              <span className={secondaryTextClass}>
                {t('search.sidebar.legalStatusOptions.active', {
                  defaultValue: 'Active',
                })}
              </span>
            );
          }
          return <LegalStatusBadge status={status} />;
        },
        size: 110,
      },
      {
        accessorKey: 'city',
        header: () => <span>{t('search.table.city')}</span>,
        // Flag before the city: with the country scope on Global, "Cham"
        // alone could be the one in Bavaria or the one in Zug.
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              className={`${secondaryTextClass} flex min-w-0 items-center gap-1.5 max-w-[100px] lg:max-w-[150px]`}
            >
              <CompanyCountryFlag
                countryCode={companyCountryCode(row.registerType, row.euId)}
              />
              <span className="truncate">
                {(info.getValue() as string) || '—'}
              </span>
            </span>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'bundesland',
        header: () => (
          <span>
            {t(`search.table.${columnLabelKey('bundesland', 'state', country)}`)}
          </span>
        ),
        cell: (info) => (
          <span
            className={`${secondaryTextClass} truncate block max-w-[100px] lg:max-w-[150px]`}
          >
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 120,
      },
      {
        accessorKey: 'rechtsform',
        header: () => (
          <span>{t('search.table.rechtsform', 'Legal form')}</span>
        ),
        cell: (info) => {
          const val = info.getValue() as string | null;
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[180px]`}
              title={val || undefined}
            >
              {val || '—'}
            </span>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'industrySector',
        header: () => <span>{t('search.table.industry')}</span>,
        cell: (info) => {
          const val = info.getValue() as string;
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[200px]`}
              title={val}
            >
              {val || '—'}
            </span>
          );
        },
        size: 150,
      },
      {
        accessorKey: 'naceCode',
        header: () => <span>{t('search.table.naceCode')}</span>,
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: 'employees',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('employees')}
          >
            <span>{t('search.table.employees')}</span>
            <SortIcon field="employees" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val === null || val === undefined)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span className={secondaryTextClass}>
              {numberFormatter.format(val)}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'totalRevenue',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('revenue')}
          >
            <span>{t('search.table.revenue')}</span>
            <SortIcon field="revenue" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val === null || val === undefined)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span className={`${secondaryTextClass} whitespace-nowrap`}>
              {fmt.compactCurrency.format(val)}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'profit',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('profit')}
          >
            <span>{t('search.table.profit')}</span>
            <SortIcon field="profit" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val === null || val === undefined)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span className={secondaryTextClass}>
              {fmt.compactCurrency.format(val)}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'domain',
        header: () => <span>{t('search.table.website')}</span>,
        cell: (info) => {
          const val = info.getValue() as string | null;
          if (!val)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <a
              href={`https://${val}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[120px] lg:max-w-40"
              title={val}
              onClick={(e) => e.stopPropagation()}
            >
              {val}
            </a>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'email',
        header: () => <span>{t('search.table.email')}</span>,
        cell: (info) => {
          const val = info.getValue() as string | null;
          if (!val)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <a
              href={`mailto:${val}`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[120px] lg:max-w-[180px]"
              title={val}
              onClick={(e) => e.stopPropagation()}
            >
              {val}
            </a>
          );
        },
        size: 150,
      },
      {
        accessorKey: 'registerType',
        header: () => <span>{t('search.table.registerType')}</span>,
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: 'registerId',
        header: () => (
          <span>
            {t(
              `search.table.${columnLabelKey('registerId', 'registerId', country)}`,
            )}
          </span>
        ),
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 120,
      },
      {
        accessorKey: 'registerCourt',
        header: () => (
          <span>
            {t(
              `search.table.${columnLabelKey('registerCourt', 'registerCourt', country)}`,
            )}
          </span>
        ),
        cell: (info) => (
          <span
            className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[160px]`}
            title={(info.getValue() as string) || undefined}
          >
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'kreis',
        header: () => <span>{t('search.table.kreis')}</span>,
        cell: (info) => (
          <span
            className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[160px]`}
            title={(info.getValue() as string) || undefined}
          >
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'postalCode',
        header: () => <span>{t('search.table.postalCode')}</span>,
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'cpvCodes',
        header: () => <span>{t('search.table.cpvCodes')}</span>,
        cell: (info) => {
          const val = info.getValue() as string[] | null;
          const text = val?.length ? val.join(', ') : '';
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[140px] lg:max-w-[200px]`}
              title={text || undefined}
            >
              {text || '—'}
            </span>
          );
        },
        size: 160,
      },
    ];
    return defs.map((c) => {
      const key = (c as { accessorKey?: string }).accessorKey;
      if (!key || !MOBILE_HIDE.has(key)) return c;
      const existingMeta =
        (c as { meta?: Record<string, unknown> }).meta ?? {};
      return {
        ...c,
        meta: {
          ...existingMeta,
          headerClassName: cn(
            'hidden lg:table-cell',
            existingMeta.headerClassName as string | undefined,
          ),
          cellClassName: cn(
            'hidden lg:table-cell',
            existingMeta.cellClassName as string | undefined,
          ),
        },
      };
    });
  }, [offset, sort, t, fmt, handleSort, country, locale, results, onOpenPreview]);

  // Show up to the current page size (rows-per-page dropdown can raise it
  // above the old fixed 50).
  const displayedResults = useMemo(
    () => results.slice(0, limit),
    [results, limit],
  );
  // Mobile shows 10 cards per page. Slice independently so the brief window
  // before the mobile-limit-override useEffect fires (SSR limit is 50) doesn't
  // flash 50 cards.
  const mobileResults = useMemo(
    () => results.slice(0, MOBILE_PAGE_SIZE),
    [results],
  );

  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-background lg:overflow-hidden">
      {/* Toolbar (columns / export / enrich) is desktop-only — hidden on mobile
          so the export and enrichment actions don't show there. */}
      <div className="hidden lg:block border-b bg-muted/5 shrink-0">
          <div className="flex items-center gap-1.5 px-2 lg:px-3 py-1.5">
            {/* Columns picker is shown to everyone; customizing is Pro/Admin
                only — non-entitled users get the "Pro feature" prompt. */}
            <ColumnsDropdown
              t={t}
              country={country}
              columnVisibility={columnVisibility}
              onChange={handleColumnVisibilityChange}
              locked={!canCustomizeColumns}
              onLockedClick={() => openProDialog('columns')}
            />
            {/* Add-to-list: the whole reason row selection exists. With
                nothing ticked it adds every company on this page, which is
                what "add these results to a list" means to someone who has
                just filtered the register. */}
            <AddToListMenu
              euIds={
                selectedEuIds.length
                  ? selectedEuIds
                  : displayedResults.map((r) => r.euId)
              }
              className="h-auto min-h-7 py-1 text-[11px] gap-1 rounded-full"
              label={
                selectedEuIds.length
                  ? t('lists.addTo.buttonMany', {
                      count: selectedEuIds.length,
                      defaultValue: 'Add {{count}} to list',
                    })
                  : t('lists.addTo.buttonPage', {
                      count: displayedResults.length,
                      defaultValue: 'Add page to list',
                    })
              }
              onAdded={() => setRowSelection({})}
            />
            {selectedEuIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto min-h-7 py-1 text-[11px] rounded-full text-muted-foreground"
                onClick={() => setRowSelection({})}
              >
                {t('search.clearSelection', {
                  count: selectedEuIds.length,
                  defaultValue: 'Clear {{count}} selected',
                })}
              </Button>
            )}
            <div className="hidden lg:block lg:flex-1" />
            {/* Export is visible to everyone; the action itself is Pro/Admin
                only — non-entitled users get the "Pro feature" prompt. */}
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 whitespace-normal text-left leading-tight rounded-full"
              onClick={handleExportClick}
              disabled={results.length === 0}
            >
              <Download className="h-3 w-3 shrink-0" />
              {t('search.exportExcel')}
            </Button>
            {/* Enrich buttons are visible to everyone; the actions are Pro/Admin
                only — non-entitled users get the "Pro feature" prompt. */}
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 whitespace-normal text-left leading-tight rounded-full"
              onClick={handleEnrichWebsites}
              disabled={isEnrichingWebsites || results.length === 0}
            >
              <Globe className="h-3 w-3 shrink-0" />
              {isEnrichingWebsites
                ? t('search.enriching')
                : t('search.enrichWebsites')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 whitespace-normal text-left leading-tight rounded-full"
              onClick={handleEnrichEmployees}
              disabled={isEnrichingEmployees || results.length === 0}
            >
              <Users className="h-3 w-3 shrink-0" />
              {isEnrichingEmployees
                ? t('search.enriching')
                : t('search.enrichEmployees')}
            </Button>
          </div>
        </div>

      <div className="flex-1 min-h-0 min-w-0 relative lg:overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Desktop: rich DataGrid table */}
            <div className="hidden lg:block h-full">
              <TableImplementation
                previewEuId={previewEuId}
                onOpenPreview={onOpenPreview}
                data={displayedResults}
                columns={columns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                total={total}
                totalLabel={totalLabel}
                locale={locale}
                offset={offset}
                limit={limit}
                onOffsetChange={onOffsetChange}
                onLimitChange={onLimitChange}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
              />
            </div>
            {/* Mobile: list of cards. The page itself scrolls (body overflow
                is only locked on desktop), so this branch flows naturally —
                no inner scroll container with a height it can't compute.
                Cards include the first line of the AI summary when available;
                pagination sits below the list. */}
            <div className="lg:hidden p-3">
              {mobileResults.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                  {t('search.noResults', { query: '' })}
                </div>
              ) : (
                <SearchInteractionProvider>
                  <div className="flex flex-col gap-3">
                    {mobileResults.map((r) => {
                      const card: CompanyCardData = {
                        euId: r.euId,
                        name: r.name,
                        registerId: r.registerId,
                        registerType: r.registerType,
                        registerCourt: r.registerCourt,
                        city: r.city,
                        postalCode: r.postalCode,
                        totalRevenue: r.totalRevenue,
                        profit: r.profit,
                        employees: r.employees,
                        latestYear: null,
                        faviconUrl: r.faviconUrl,
                        aiSummary: r.aiSummary,
                      };
                      return (
                        <CompanyCard key={r.euId} company={card} view="list" />
                      );
                    })}

                    {onOffsetChange && total > 0 && (
                      <MobilePagination
                        offset={offset}
                        limit={MOBILE_PAGE_SIZE}
                        total={total}
                        totalLabel={totalLabel}
                        onOffsetChange={onOffsetChange}
                      />
                    )}
                  </div>
                </SearchInteractionProvider>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={showProDialog} onOpenChange={setShowProDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {/* Columns are free for any logged-in user — the lock only fires
                  for guests, so that prompt asks them to create an account. */}
              {proDialogFeature === 'columns'
                ? t('search.accountRequired.title', 'Konto erforderlich')
                : t('search.proFeature.title', 'Pro-Funktion')}
            </DialogTitle>
            <DialogDescription>
              {proDialogFeature === 'columns'
                ? t(
                    'search.accountRequired.descriptionColumns',
                    'Erstellen Sie ein kostenloses Konto, um die angezeigten Spalten anzupassen.',
                  )
                : proDialogFeature === 'enrich'
                  ? t(
                      'search.proFeature.descriptionEnrich',
                      'Die Datenanreicherung ist nur für Pro-Nutzer verfügbar. Mit einem Pro-Konto können Sie Websites und Mitarbeiterzahlen anreichern.',
                    )
                  : t(
                      'search.proFeature.description',
                      'Der Excel-Export ist nur für Pro-Nutzer verfügbar. Mit einem Pro-Konto können Sie Suchergebnisse exportieren.',
                    )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-md"
              onClick={() => setShowProDialog(false)}
            >
              {t('common.buttons.close', 'Schließen')}
            </Button>
            {proDialogFeature === 'columns' ? (
              <Button
                className="rounded-md"
                onClick={() => {
                  setShowProDialog(false);
                  window.location.href = `/auth/login?screen_hint=signup&returnTo=${encodeURIComponent(window.location.pathname)}`;
                }}
              >
                {t('search.accountRequired.cta', 'Konto erstellen')}
              </Button>
            ) : (
              <Button asChild className="rounded-md">
                <LocaleLink href="/plans" onClick={() => setShowProDialog(false)}>
                  {t('search.proFeature.cta', 'Pläne ansehen')}
                </LocaleLink>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        total={total}
        totalLabel={totalLabel}
        t={t}
        isExporting={isExporting}
        exportError={exportError}
        onExport={runExport}
        country={country}
      />
    </div>
  );
}

// Excel export dialog (Pro/Admin): pick which columns to include and how many
// results to export (preset counts, capped at EXPORT_MAX and the total found).
// The form state lives in ExportDialogBody, which only mounts while the dialog
// is open — so every open starts from fresh defaults without a reset effect.
function ExportDialog({
  open,
  onOpenChange,
  total,
  totalLabel,
  t,
  isExporting,
  exportError,
  onExport,
  country,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  total: number;
  totalLabel: string;
  t: ReturnType<typeof useTranslation>['t'];
  isExporting: boolean;
  exportError: boolean;
  onExport: (columnIds: string[], count: number) => void;
  country?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <ExportDialogBody
          total={total}
          totalLabel={totalLabel}
          t={t}
          isExporting={isExporting}
          exportError={exportError}
          onCancel={() => onOpenChange(false)}
          onExport={onExport}
          country={country}
        />
      </DialogContent>
    </Dialog>
  );
}

function ExportDialogBody({
  total,
  totalLabel,
  t,
  isExporting,
  exportError,
  onCancel,
  onExport,
  country,
}: {
  total: number;
  totalLabel: string;
  t: ReturnType<typeof useTranslation>['t'];
  isExporting: boolean;
  exportError: boolean;
  onCancel: () => void;
  onExport: (columnIds: string[], count: number) => void;
  country?: string;
}) {
  const { i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => createFormatters(i18n.language).fullNumber,
    [i18n.language],
  );
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    country === 'CH' ? SWISS_DEFAULT_EXPORT_COLUMNS : DEFAULT_EXPORT_COLUMNS,
  );
  const [selectedCount, setSelectedCount] = useState<number>(
    total > 100 ? 1000 : 100,
  );

  const toggleColumn = (id: string, checked: boolean) => {
    setSelectedColumns((prev) =>
      checked ? [...prev, id] : prev.filter((c) => c !== id),
    );
  };

  // A preset is offered only if the previous (smaller) preset doesn't already
  // cover every result — so we never show 10,000 when there are 200 matches,
  // but always keep at least one option that exports everything available.
  const presetEnabled = (idx: number) =>
    total > (EXPORT_COUNT_PRESETS[idx - 1] ?? 0);
  const exportCount = Math.min(selectedCount, total, EXPORT_MAX);
  const canSubmit = selectedColumns.length > 0 && total > 0 && !isExporting;

  return (
    <>
        <DialogHeader>
          <DialogTitle>{t('search.exportDialog.title', 'Excel-Export')}</DialogTitle>
          <DialogDescription>
            {t(
              'search.exportDialog.description',
              'Wählen Sie die Spalten und die Anzahl der Ergebnisse für den Export.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t('search.columns', 'Spalten')}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {EXPORTABLE_COLUMNS.map((col) => {
                const checked = selectedColumns.includes(col.id);
                return (
                  <label
                    key={col.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => toggleColumn(col.id, v === true)}
                    />
                    <span>{exportColumnLabel(col.id, t, country)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t('search.exportDialog.countLabel', 'Anzahl der Ergebnisse')}
            </div>
            <div className="flex flex-wrap gap-2">
              {EXPORT_COUNT_PRESETS.map((preset, idx) => {
                const enabled = presetEnabled(idx);
                return (
                  <Button
                    key={preset}
                    type="button"
                    size="sm"
                    variant={selectedCount === preset ? 'primary' : 'outline'}
                    disabled={!enabled || isExporting}
                    onClick={() => setSelectedCount(preset)}
                  >
                    {numberFormatter.format(preset)}
                  </Button>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {t('search.exportDialog.summary', {
                count: numberFormatter.format(exportCount),
                total: totalLabel,
                defaultValue:
                  '{{count}} von {{total}} Ergebnissen werden exportiert',
              })}
            </div>
          </div>

          {exportError && (
            <div className="text-sm text-destructive">
              {t(
                'search.exportDialog.error',
                'Export fehlgeschlagen. Bitte erneut versuchen.',
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isExporting}>
            {t('common.buttons.cancel', 'Abbrechen')}
          </Button>
          <Button
            onClick={() => onExport(selectedColumns, exportCount)}
            disabled={!canSubmit}
          >
            {isExporting && <Loader2 className="h-3 w-3 animate-spin" />}
            {isExporting
              ? t('search.exporting', 'Exportiere…')
              : t('search.exportExcel')}
          </Button>
        </DialogFooter>
    </>
  );
}

function MobilePagination({
  offset,
  limit,
  total,
  totalLabel,
  onOffsetChange,
}: {
  offset: number;
  limit: number;
  total: number;
  totalLabel: string;
  onOffsetChange: (offset: number) => void;
}) {
  const { t } = useTranslation();
  const cappedTotal = Math.min(total, 5000);
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(cappedTotal / limit));
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  return (
    <div className="flex items-center justify-between gap-3 mt-2 pb-4">
      <Button
        variant="outline"
        size="sm"
        disabled={isFirst}
        onClick={() => onOffsetChange(Math.max(0, offset - limit))}
      >
        {t('common.buttons.previous', 'Zurück')}
      </Button>
      <span className="text-xs text-muted-foreground text-center">
        <span className="block">
          {t('search.showingRange', {
            from: offset + 1,
            to: Math.min(offset + limit, total, 5000),
            total: totalLabel,
          })}
        </span>
        {t('common.labels.pagination', {
          current: currentPage,
          total: totalPages,
          defaultValue: 'Seite {{current}} von {{total}}',
        })}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={isLast}
        onClick={() => onOffsetChange(offset + limit)}
      >
        {t('common.buttons.next', 'Weiter')}
      </Button>
    </div>
  );
}

// Logged-in users' column picker. Desktop only — the mobile view renders
// cards, not a column-based table, so the picker has nothing to act on there.
function ColumnsDropdown({
  t,
  country,
  columnVisibility,
  onChange,
  locked = false,
  onLockedClick,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  country?: string;
  columnVisibility: VisibilityState;
  onChange: (id: string, visible: boolean) => void;
  // When locked (non-Pro), the trigger doesn't open the menu — it calls
  // onLockedClick (which surfaces the "Pro feature" prompt) instead.
  locked?: boolean;
  onLockedClick?: () => void;
}) {
  const trigger = (
    <Button
      variant="outline"
      size="sm"
      className="hidden lg:inline-flex h-auto min-h-7 py-1 text-[11px] gap-1 rounded-full"
      onClick={locked ? onLockedClick : undefined}
    >
      <SlidersHorizontal className="h-3 w-3 shrink-0" />
      {t('search.columns', 'Spalten')}
    </Button>
  );

  if (locked) return trigger;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[60vh] overflow-y-auto"
      >
        <DropdownMenuLabel>
          {t('search.columnsLabel', 'Sichtbare Spalten')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TOGGLEABLE_COLUMNS.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            checked={!!columnVisibility[col.id]}
            // Keep the menu open so several columns can be toggled at once.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) => onChange(col.id, !!checked)}
          >
            {t(
              `search.table.${columnLabelKey(col.id, col.labelKey, country)}`,
            )}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TableImplementation({
  previewEuId,
  onOpenPreview,
  data,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  total,
  totalLabel,
  locale,
  offset,
  limit,
  onOffsetChange,
  onLimitChange,
  rowSelection,
  onRowSelectionChange,
}: {
  previewEuId?: string | null;
  onOpenPreview?: (euId: string, trigger?: HTMLElement) => void;
  data: CompanySearchResult[];
  columns: ColumnDef<CompanySearchResult>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (id: string, visible: boolean) => void;
  total: number;
  totalLabel: string;
  locale: import('@/lib/i18n-routes').Locale;
  offset: number;
  limit: number;
  onOffsetChange?: (offset: number) => void;
  onLimitChange?: (limit: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (next: RowSelectionState) => void;
}) {
  const { t } = useTranslation();
  // Offset-based pagination expressed as TanStack pagination state so the
  // shared DataGridPagination (page numbers + rows-per-page) can drive it. The
  // total is capped at 5000 for paging, matching the sidebar/mobile pagers.
  const pageSize = limit || 50;
  const pagination = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
  };
  const pageCount = Math.max(1, Math.ceil(Math.min(total, 5000) / pageSize));
  const table = useReactTable({
    data,
    columns,
    state: { columnVisibility, pagination, rowSelection },
    manualPagination: true,
    pageCount,
    // Keyed by eu_id, not row index: a selection has to survive paging and
    // re-sorting, where index means a different company.
    getRowId: (row) => row.euId,
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      onRowSelectionChange(
        typeof updater === 'function' ? updater(rowSelection) : updater,
      );
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      if (next.pageSize !== pagination.pageSize) {
        // Changing page size resets to the first page (handleLimitChange does
        // offset:0 on the parent).
        onLimitChange?.(next.pageSize);
      } else if (next.pageIndex !== pagination.pageIndex) {
        onOffsetChange?.(next.pageIndex * pagination.pageSize);
      }
    },
    // Visibility is owned by the parent (persisted to user settings); funnel
    // any table-originated change back through the same handler.
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnVisibility) : updater;
      const prevKeys = new Set(Object.keys(columnVisibility));
      for (const [id, visible] of Object.entries(next)) {
        if (!prevKeys.has(id) || columnVisibility[id] !== visible) {
          onColumnVisibilityChange(id, visible);
        }
      }
    },
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
    },
  });

  return (
    <DataGridContainer className="h-full flex flex-col border-none rounded-none overflow-hidden min-w-0 relative">
      <DataGrid<CompanySearchResult>
        table={table}
        recordCount={total}
        onRowClick={(row, event) => {
          if (
            event.button !== 0 || event.metaKey || event.ctrlKey ||
            event.shiftKey || event.altKey
          ) return;
          const position = data.findIndex((r) => r.euId === row.euId);
          trackSearchResultClick(row.euId, offset + position);
          onOpenPreview?.(row.euId, event.currentTarget);
        }}
        onRowAuxClick={(row, event) => {
          if (event.button !== 1) return;
          event.preventDefault();
          const position = data.findIndex((r) => r.euId === row.euId);
          trackSearchResultClick(row.euId, offset + position);
          const slug = generateCompanySlug(
            {
              name: row.name,
              sitz: row.city,
              registerCourt: row.registerCourt,
              registerType: row.registerType,
              registerId: row.registerId,
            },
            locale,
          );
          window.open(slug, '_blank', 'noopener');
        }}
        rowClassName={(row) =>
          row.euId === previewEuId ? 'bg-primary/5' : undefined
        }
        tableLayout={{
          headerSticky: true,
          headerBorder: true,
          rowBorder: true,
          stripped: false,
          dense: true,
          width: 'fixed',
          columnsResizable: true,
          cellBorder: false,
        }}
        tableClassNames={{
          base: 'min-w-full lg:w-max',
          header:
            'bg-gray-50/80 dark:bg-zinc-900/90 backdrop-blur-sm text-[10px] font-bold text-gray-500 dark:text-zinc-300 uppercase tracking-widest',
          headerRow: 'h-10',
          body: 'text-gray-700 dark:text-zinc-300 font-sans',
          bodyRow:
            'h-9 hover:bg-blue-50/20 dark:hover:bg-white/5 border-b dark:border-white/10 transition-colors',
        }}
      >
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden lg:overflow-auto pb-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:pb-0 scroll-pb-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:scroll-pb-0 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-zinc-700">
          <DataGridTable />
        </div>
        {/* Bottom pagination bar with rows-per-page dropdown, same as /vergabe. */}
        <div className="shrink-0 border-t px-3 py-2">
          <DataGridPagination
            sizes={[25, 50, 100]}
            totalLimit={5000}
            info={t('search.showingRange', {
              from: data.length ? offset + 1 : 0,
              to: Math.min(offset + data.length, total, 5000),
              total: totalLabel,
            })}
          />
        </div>
      </DataGrid>
    </DataGridContainer>
  );
}
